import React, { useState, useEffect, useRef } from 'react'
import { IconButton, SelectChangeEvent, Select, MenuItem } from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import TextField from '@mui/material/TextField'
import styles from './EFTablePagination.module.scss'
import classNames from 'classnames/bind'
import { currentTheme, pageSetting, setPageSize, setPageOptions } from '~/redux/features'
import { useAppSelector } from '~/app/hooks'
import { ALL_NS } from '~/i18n/namespaces'
import { useTranslation } from 'react-i18next'
import { TranslateToSelectTypeArray } from '~/utils'
import { EFTablePaginationProps } from './EFTablePagination.model'
import { paginate } from './helpers'
import { DATA_TABLE_DEFAULTS } from '~/constants/dataTableSettings'
import { useDispatch } from 'react-redux'
import { EFNumericField } from '../EFTextField'
import { useSearchParams } from 'react-router-dom'
import { useMobileView } from '~/hooks/useMobileView'

const cx = classNames.bind(styles)

export function EFTablePagination(props: EFTablePaginationProps) {
  const theme = useAppSelector(currentTheme)
  const isMobile = useMobileView()
  const dispatch = useDispatch()
  const { t } = useTranslation(ALL_NS)
  const { count, currentPage, totalPages, onPageChange } = props
  const [searchParams, setSearchParams] = useSearchParams()

  const customPageSizeRef = useRef(null)
  const { pageSize, pageOptions } = useAppSelector(pageSetting)

  const [pages, setPages] = useState([0])
  const [customPageSize, setCustomPageSize] = useState('')
  const [selectOpen, setSelectOpen] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true) // Track initial load

  const handleChangeRowsPerPage = (event: SelectChangeEvent<unknown>) => {
    const newPageSize = parseInt(event.target.value as string)
    dispatch(setPageSize(newPageSize))
  }

  const handleCustomPageSizeKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && customPageSize) {
      const size = parseInt(customPageSize)
      if (size > 0) {
        dispatch(setPageOptions(size))
        setSelectOpen(false)
      }
    }
  }

  const handleCustomInputClick = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  const handleCustomInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    setCustomPageSize(event.target.value)
  }

  function handlePageChange(page: number) {
    if (page < 0 || page > totalPages - 1) return
    onPageChange(page)
  }

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    const value = (e.target as HTMLInputElement).value

    if (e.key === 'Enter' && value) {
      handlePageChange(parseInt(value) - 1)
    }
  }

  // Handle query string pageSize on mount
  useEffect(() => {
    const queryPageSize = searchParams.get('pageSize')
    if (queryPageSize) {
      const parsedPageSize = parseInt(queryPageSize)
      if (!isNaN(parsedPageSize) && parsedPageSize > 0) {
        dispatch(setPageOptions(parsedPageSize)) // Set pageSize in Redux store
      }
    }
    setIsInitialLoad(false) // Mark initial load as complete
  }, [searchParams, dispatch])

  // Update query string when pageSize changes
  useEffect(() => {
    if (!isInitialLoad && pageSize) {
      searchParams.set('pageSize', pageSize.toString())
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, searchParams, setSearchParams, isInitialLoad])

  useEffect(() => {
    setPages(paginate(currentPage + 1, totalPages, isMobile))
  }, [currentPage, pageSize, count, totalPages, isMobile])

  useEffect(() => {
    setCustomPageSize(pageSize?.toString())
  }, [pageSize])

  return (
    <div className={cx('pagination-container', theme)}>
      <div className={cx('pagination-block')}>
        <span className={cx('item-per-page-text')}>{t('Label_ItemsPerPage')}</span>
        <span className={cx('page-size-text')}>{t('Page size')}</span>
        <Select
          value={pageSize}
          onChange={handleChangeRowsPerPage}
          open={selectOpen}
          onOpen={() => setSelectOpen(true)}
          onClose={() => setSelectOpen(false)}
          className='form-select w-auto'
          id='row-per-page'
          sx={{ div: { paddingY: 1 } }}
        >
          {TranslateToSelectTypeArray(
            pageOptions ?? DATA_TABLE_DEFAULTS.PageSizeOptions
          ).map((option, index) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                display:
                  index < DATA_TABLE_DEFAULTS.PageSizeOptions.length ? 'block' : 'none'
              }}
            >
              {option.name}
            </MenuItem>
          ))}
          <MenuItem
            sx={{ borderTop: 'var(--border)', padding: '1px 8px' }}
            onClick={handleCustomInputClick}
          >
            <EFNumericField
              ref={customPageSizeRef}
              name='customPageSize'
              value={customPageSize}
              onChange={handleCustomInputChange}
              onKeyDown={event => {
                event.stopPropagation()
                handleCustomPageSizeKeyPress(event)
              }}
              onClick={handleCustomInputClick}
              sx={{
                'input::-webkit-outer-spin-button,input::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0
                },
                'input[type=number]': {
                  MozAppearance: 'textfield'
                },
                '.MuiInputBase-root': {
                  fontSize: '0.875rem',
                  height: 30,
                  width: 70,
                  margin: 0,
                  '.MuiInputBase-input': {
                    padding: '1rem 0.4rem'
                  }
                }
              }}
            />
          </MenuItem>
        </Select>
      </div>
      <div className={cx('pagination-block')}>
        <div>
          <IconButton
            id='btn-first-page'
            className={cx('btn-first-page')}
            onClick={() => handlePageChange(0)}
          >
            <FirstPageIcon className={cx('icon-small', theme)} />
          </IconButton>
          <IconButton
            id='btn-previous-page'
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <KeyboardArrowLeftIcon className={cx('icon-small', theme)} />
          </IconButton>

          {pages.map((page, index) =>
            page ? (
              <IconButton
                id={'btn-page-number-' + page}
                className={cx(theme)}
                onClick={() => handlePageChange(page - 1)}
                key={index}
              >
                <span
                  role='button'
                  className={cx(
                    page - 1 === currentPage && 'active',
                    'icon-small',
                    theme
                  )}
                >
                  {page}
                </span>
              </IconButton>
            ) : (
              <span key={index}>…</span>
            )
          )}

          <IconButton
            id='btn-next-page'
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <KeyboardArrowRightIcon className={cx('icon-small', theme)} />
          </IconButton>
          <IconButton
            id='btn-last-page'
            className={cx('btn-last-page')}
            onClick={() => handlePageChange(totalPages - 1)}
          >
            <LastPageIcon className={cx('icon-small', theme)} />
          </IconButton>
        </div>
        <div className={cx('pagination-block')}>
          <span
            title={t('Label_GotoPage')}
            className={cx('item-per-page-text')}
          >
            {t('Label_GotoPage')}
          </span>
          <TextField
            onKeyDown={handleKeywordKeyPress}
            className={cx('goto-textfield', theme)}
            placeholder={isMobile ? t('Label_GotoPage') : ''}
            type='number'
            sx={{
              'input::-webkit-outer-spin-button,input::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0
              },
              'input[type=number]': {
                MozAppearance: 'textfield'
              },
              marginLeft: 1,
              '.MuiInputBase-root': {
                height: 30,
                width: 60,
                margin: 0,
                '.MuiInputBase-input': {
                  padding: '1rem 0.4rem'
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
